/**
 * boh data service
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { filter, find, get, isEqual } from 'lodash';
import { Observable, observableThrowError, rx } from '@shared/rx';
import { environment } from '../../../../environments/environment';
import { NO_PERMISSION_STATUS } from '@shared/constants';
import { HttpErrorService } from '../http-error';
import { batchRequestMap } from '@app/shared/rx-operators';
import { of } from 'rxjs';
import { IStaffUserInfo } from './staff-data.types';
import { isObjectIdValid } from '@shared/utils/common-utils';

@Injectable()
export class StaffDataService {
  public static UTILITY_TYPE: string = 'StaffDataService';

  private get baseUrl(): string {
    return environment.xviBackendURI;
  }

  public constructor(
    private http: HttpClient,
    public httpErrorService: HttpErrorService,
  ) {}

  public getCurrentUserInfo(
    staffId: string,
    projection: string,
  ): Observable<IStaffUserInfo | {}> {
    return this.http
      .get(`${this.baseUrl}/employee/${staffId}?projection=${projection}`)
      .pipe(rx.catchError(() => of({})));
  }

  public batchGetUserInfo(
    staffIds: string[],
    batchSize: number = 6,
  ): Observable<IStaffUserInfo[]> {
    const projectionFields = 'EmployeeId,Name';
    const correctStaffIds = filter(staffIds, (staffId) =>
      isObjectIdValid(staffId),
    );

    return batchRequestMap(
      correctStaffIds,
      (staffId: string) => this.getCurrentUserInfo(staffId, projectionFields),
      batchSize,
    ).pipe(
      rx.reduce(
        (allUserInfo, currentUserInfo) => [...allUserInfo, currentUserInfo],
        [],
      ),
      this.catchHttpErrorAndPickErrorList(),
    );
  }

  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const errorData = get(error, 'errors', {});
          const hasNoPermissionError = find(errorData, (errorItem) =>
            isEqual(errorItem.status, NO_PERMISSION_STATUS),
          );
          if (hasNoPermissionError) {
            return this.httpErrorService.catchNoPermissionHttpError(errorData);
          }

          return observableThrowError(errorData);
        }),
      );
}
